import { z } from 'zod';
import { ProvisioningConsumablesFiltersValidator } from '../collators/schema';
import { validateHttpUrl } from '../utils/zod.utils';

export const HelpLinkValidator = z
  .object({
    label: z.string().trim().min(1),
    url: z.string().trim().min(1).superRefine(validateHttpUrl),
    icon: z.string().trim().min(1).optional(),
  })
  .strict();

export const HelpLinksValidator = z
  .object({
    items: z.array(HelpLinkValidator).optional(),
    adminItems: z.array(HelpLinkValidator).optional(),
  })
  .strict();

export const NotificationEmailColorsValidator = z
  .object({
    primary: z.string().trim().min(1).max(100).optional(),
    secondary: z.string().trim().min(1).max(100).optional(),
    text: z.string().trim().min(1).max(100).optional(),
    footerText: z.string().trim().min(1).max(100).optional(),
  })
  .strict();

export const DEFAULT_NOTIFICATION_EMAIL_LOGO_MAX_SIZE_MB = 3;

export function getNotificationEmailLogoMaxSizeMb(
  configuredValue?: number,
): number {
  if (
    typeof configuredValue === 'number' &&
    Number.isFinite(configuredValue) &&
    configuredValue > 0
  ) {
    return configuredValue;
  }
  return DEFAULT_NOTIFICATION_EMAIL_LOGO_MAX_SIZE_MB;
}

export function createNotificationEmailTemplateValidator(
  configuredLogoMaxSizeMb?: number,
) {
  const maxLogoBytes =
    getNotificationEmailLogoMaxSizeMb(configuredLogoMaxSizeMb) * 1024 * 1024;

  return z
    .object({
      appName: z.string().trim().min(1).max(200).optional(),
      footer: z.string().max(20_000).optional(),
      color: NotificationEmailColorsValidator.optional(),
      logoMaxWidth: z.number().int().optional(),
      logoMaxHeight: z.number().int().optional(),
      logoSizeMode: z.enum(['percent', 'pixels']).optional(),
      logoSizePercent: z.string().optional(),
      logoBase64: z
        .string()
        .max(Math.ceil(maxLogoBytes / 3) * 4)
        .regex(
          /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/,
        )
        .optional(),
      logoMimeType: z.enum(['image/png', 'image/jpeg']).optional(),
    })
    .strict()
    .superRefine((value, context) => {
      if (
        value.appName === undefined &&
        value.footer === undefined &&
        value.color === undefined &&
        value.logoMaxWidth === undefined &&
        value.logoMaxHeight === undefined &&
        value.logoSizeMode === undefined &&
        value.logoSizePercent === undefined &&
        value.logoBase64 === undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Configure branding or a logo override',
        });
      }
      if (
        (value.logoBase64 === undefined) !==
        (value.logoMimeType === undefined)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Logo data and MIME type must be configured together',
          path: ['logoBase64'],
        });
      }
      if (value.logoBase64) {
        let padding = 0;
        if (value.logoBase64.endsWith('==')) padding = 2;
        else if (value.logoBase64.endsWith('=')) padding = 1;
        const decodedBytes = (value.logoBase64.length * 3) / 4 - padding;
        if (decodedBytes > maxLogoBytes) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Logo must be ${getNotificationEmailLogoMaxSizeMb(
              configuredLogoMaxSizeMb,
            )} MB or smaller`,
            path: ['logoBase64'],
          });
        }
      }
    });
}

export const NotificationEmailTemplateValidator =
  createNotificationEmailTemplateValidator();

export type NotificationEmailTemplateConfig = z.infer<
  typeof NotificationEmailTemplateValidator
>;

export const NOTIFICATIONS_CONFIG_NAMESPACE = 'notifications';
export const NOTIFICATION_EMAIL_STYLE_SETTINGS_KEY = 'email-style-settings';
export const NOTIFICATION_EMAIL_PROVIDER_SETTINGS_KEY =
  'email-provider-settings';

export function createNotificationEmailConfigurationZod(
  configuredLogoMaxSizeMb?: number,
) {
  return z.union([
    z.object({}).strict(),
    createNotificationEmailTemplateValidator(configuredLogoMaxSizeMb),
  ]);
}

export const notificationEmailConfigurationZod =
  createNotificationEmailConfigurationZod();

export const ConfigValidator = z.object({
  fe: z
    .object({
      helpLinks: HelpLinksValidator.optional(),
      developerTools: z
        .object({
          enabled: z.boolean(),
        })
        .optional(),
    })
    .optional(),
  search: z.object({
    'provisioning-consumables': z.object({
      filters: ProvisioningConsumablesFiltersValidator,
    }),
  }),
});

export type ConfigSchema = z.infer<typeof ConfigValidator>;

export const defaultConfig: DeepPartial<ConfigSchema> = {};

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};
